import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import LinearProgress from '@mui/material/LinearProgress';
import { useTranslation } from 'react-i18next';
import { ServiceUserDto } from 'models/dtoTypes';
import { GetServiceUsers, UpdateServiceUser } from 'services/user';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { TextField } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const PREFIX = 'ServiceApiWidget';

const classes = {
  root: `${PREFIX}-root`,
  addCredit: `${PREFIX}-addCredit`,
  button: `${PREFIX}-button`,
};

const Root = styled('div')(({ theme }) => ({
  width: 700,
  margin: theme.spacing(1),
  padding: theme.spacing(1),
  border: '1px solid #666',

  [`& .${classes.addCredit}`]: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    textAlign: 'right',
    border: '1px solid #666',
  },
  [`& .${classes.button}`]: {
    margin: theme.spacing(1),
    marginTop: theme.spacing(3),
    textAlign: 'right',
  },
}));

interface IProps {
  organisationId: string;
}

function ServiceApiWidget(props: IProps): JSX.Element {
  const { organisationId } = props;
  const [serviceUser, setServiceUser] = useState<ServiceUserDto>();
  const [expiredDate, setExpiredDate] = useState<Date | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('default');

  useEffect(() => {
    async function fetchServiceUsers() {
      if (!organisationId) {
        setError('OrganisationId is missing');
        return;
      }
      setLoading(true);
      try {
        const users = await GetServiceUsers(organisationId);
        if (users?.length) {
          setServiceUser(users[0]);
          setExpiredDate(users[0].expiresAt ? new Date(users[0].expiresAt) : null);
        }
      } catch (e) {
        console.error(e);
        setError(e instanceof Error ? e.message : String(e));
      }
      setLoading(false);
    }
    fetchServiceUsers();
  }, [organisationId]);

  async function handleUpdateExpireDate() {
    if (!organisationId) {
      setError('OrganisationId is missing');
      return;
    }
    if (!serviceUser) {
      setError('ServiceUser is missing');
      return;
    }
    if (!expiredDate) {
      setError('None Zero value required');
      return;
    }
    setLoading(true);
    try {
      await UpdateServiceUser(organisationId,{
        serviceUserId: serviceUser.id,
        expiresAt: expiredDate?.toISOString(),
      });
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : String(e));
    }
    setLoading(false);
  }

  const renderServiceUser = () => {
    if (loading) {
      return 'loading...';
    }
    else if (!serviceUser) {
      return 'No Service User found';
    }
    return (
      <Stack direction="column" spacing={1}>
        <div
          style={{
            display: 'flex',
            fontWeight: 'bold',
            lineHeight: '40px',
          }}
        >
          <label
            style={{
              width: '50%',
              paddingLeft: '5px',
              fontSize: '14px',
              color: '#666666',
            }}
          >
            {t('Client ID')}:
          </label>
          <div style={{ flex: 1 }}>{serviceUser?.aadApplicationId}</div>
        </div>
        <div
          style={{
            display: 'flex',
            fontWeight: 'bold',
            lineHeight: '40px',
          }}
        >
          <label
            style={{
              width: '50%',
              paddingLeft: '5px',
              fontSize: '14px',
              color: '#666666',
            }}
          >
            {t('Client Secret')}:
          </label>
          <div style={{ flex: 1 }}>XXX-XXX-XXX</div>
        </div>
        <div
          style={{
            display: 'flex',
            lineHeight: '40px',
          }}
        >
          <label
            style={{
              width: '50%',
              paddingLeft: '5px',
              fontSize: '14px',
              color: '#666666',
            }}
          >
            {t('Expires At')}:
          </label>
          <div style={{ flex: 1 }}>
            <FormControl error={Boolean(error)}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  inputFormat="yyyy-MM-dd"
                  disabled={loading}
                  value={expiredDate}
                  onChange={(newValue) => setExpiredDate(newValue)}
                  renderInput={(params: any) => <TextField {...params} />}
                />
              </LocalizationProvider>
            </FormControl>
          </div>
        </div>
        <div className={classes.button}>
          <Button
            variant="contained"
            disabled={loading}
            color="primary"
            autoFocus
            onClick={handleUpdateExpireDate}
          >
            {t('Update Expiry Date')}
          </Button>
        </div>
      </Stack>
    );
  };

  return (
    <Root>
      {loading && <LinearProgress />}
      {error && (
        <Snackbar open={true} autoHideDuration={5000}>
          <Alert severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
      )}
      {renderServiceUser()}
    </Root>
  );
}

export default ServiceApiWidget;
